import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Helmet } from 'react-helmet-async';
import { adminApi, type CreateCompetitionDto } from '../../api/adminApi';
import { PageHeader } from '../../components/PageHeader';
import { COMPETITION_STATUS_LABELS, type Competition, type CompetitionStatus } from '../../types/domain';
import { useImpersonation, withUnit } from '../../hooks/useImpersonation';
import { formatDateCz } from '../../utils/dateFormat';

const STATUS_COLOR: Record<CompetitionStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  LIVE: 'error',
  FINISHED: 'success',
};

export const CompetitionsPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { effectiveUnitId, isImpersonating, impersonatedUnit } = useImpersonation();
  const [creating, setCreating] = useState(false);

  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['admin', 'competitions', effectiveUnitId ?? 'all'],
    queryFn: () => adminApi.listCompetitions(effectiveUnitId),
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: adminApi.listTemplates,
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateCompetitionDto) =>
      adminApi.createCompetition({
        ...dto,
        unitId: effectiveUnitId ?? dto.unitId ?? null,
      }),
    onSuccess: (created) => {
      enqueueSnackbar('Soutěž vytvořena', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competitions'] });
      setCreating(false);
      navigate(withUnit(`/admin/competitions/${created.id}`, effectiveUnitId));
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const columns: GridColDef<Competition>[] = [
    {
      field: 'shortCode',
      headerName: 'Kód',
      width: 110,
      renderCell: ({ row }) => (
        <Box sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem' }}>{row.shortCode}</Box>
      ),
    },
    { field: 'name', headerName: 'Název', flex: 1, minWidth: 200 },
    {
      field: 'competitionDate',
      headerName: 'Datum',
      width: 130,
      renderCell: ({ row }) => formatDateCz(row.competitionDate),
    },
    { field: 'location', headerName: 'Místo', width: 180, renderCell: ({ row }) => row.location ?? '—' },
    {
      field: 'status',
      headerName: 'Stav',
      width: 130,
      renderCell: ({ row }) => (
        <Chip size="small" label={COMPETITION_STATUS_LABELS[row.status]} color={STATUS_COLOR[row.status]} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="Otevřít detail">
          <IconButton
            size="small"
            component={RouterLink}
            to={withUnit(`/admin/competitions/${row.id}`, effectiveUnitId)}
            onClick={(e) => e.stopPropagation()}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Soutěže | Admin</title>
      </Helmet>
      <PageHeader
        title="Soutěže"
        subtitle={
          isImpersonating
            ? `Soutěže jednotky ${impersonatedUnit?.name ?? ''}`
            : 'Správa soutěží vaší jednotky'
        }
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
            Nová soutěž
          </Button>
        }
      />
      {isMobile ? (
        <Stack spacing={1}>
          {isLoading && <Typography color="text.secondary">Načítám…</Typography>}
          {!isLoading && comps.length === 0 && (
            <Typography color="text.secondary">Žádné soutěže.</Typography>
          )}
          {comps.map((c) => (
            <Paper
              key={c.id}
              variant="outlined"
              sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => navigate(withUnit(`/admin/competitions/${c.id}`, effectiveUnitId))}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={600}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {formatDateCz(c.competitionDate)} {c.location && `• ${c.location}`}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip size="small" label={COMPETITION_STATUS_LABELS[c.status]} color={STATUS_COLOR[c.status]} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {c.shortCode}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 'calc(100vh - 220px)', bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={comps}
            columns={columns}
            loading={isLoading}
            disableRowSelectionOnClick
            onRowClick={({ row }) => navigate(withUnit(`/admin/competitions/${row.id}`, effectiveUnitId))}
            sx={{
              border: 0,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
          />
        </Box>
      )}

      <CreateCompetitionDialog
        open={creating}
        templates={templates}
        loading={createMutation.isPending}
        onCancel={() => setCreating(false)}
        onSubmit={(dto) => createMutation.mutate(dto)}
      />
    </>
  );
};

interface CreateCompetitionDialogProps {
  open: boolean;
  templates: { id: number; name: string; description: string | null }[];
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateCompetitionDto) => void;
}

const CreateCompetitionDialog = ({ open, templates, loading, onCancel, onSubmit }: CreateCompetitionDialogProps) => {
  const today = new Date().toISOString().substring(0, 10);
  const [form, setForm] = useState<CreateCompetitionDto>({
    name: '',
    competitionDate: today,
    location: '',
    templateId: null,
  });
  const update = (field: keyof CreateCompetitionDto, value: any) => setForm((f) => ({ ...f, [field]: value }));
  const reset = () => setForm({ name: '', competitionDate: today, location: '', templateId: null });

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: reset }}>
      <DialogTitle>Nová soutěž</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Název soutěže"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            required
            autoFocus
            placeholder="např. Okresní kolo Plamen 2026"
          />
          <TextField
            label="Datum konání"
            type="date"
            value={form.competitionDate}
            onChange={(e) => update('competitionDate', e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Místo"
            value={form.location}
            onChange={(e) => update('location', e.target.value)}
            placeholder="např. Hřiště SDH Těškovice"
          />
          <FormControl fullWidth>
            <InputLabel>Šablona (volitelně)</InputLabel>
            <Select
              value={form.templateId ?? ''}
              label="Šablona (volitelně)"
              onChange={(e) => update('templateId', e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">— Bez šablony —</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Šablona automaticky vytvoří kategorie a disciplíny.
            </Typography>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(form)}
          disabled={loading || !form.name || !form.competitionDate}
        >
          Vytvořit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
